import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  ArrowLeftIcon, 
  ClockIcon, 
  UserIcon, 
  MessageSquareIcon, 
  CheckCircleIcon, 
  AlertTriangleIcon, 
  TrendingUpIcon,
  BuildingIcon,
  HistoryIcon,
  UserCheckIcon,
  SettingsIcon,
  ImageIcon,
  XIcon,
  LinkIcon,
  Link2OffIcon,
  GitBranchIcon,
  RefreshCwIcon
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint, API_URL } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";

interface Ticket {
  id: string;
  parent_id?: string | null;
  title: string;
  description: string;
  type: string;
  category: string;
  subcategory?: string;
  priority: string;
  customer_priority?: string | null;
  status: string;
  impact: string;
  urgency: string;
  contact_phone?: string | null;
  preferred_contact_time?: string | null;
  tracking_status?: string;
  queue_level?: string;
  transfer_count?: number;
  requester: { id: number; name: string; email: string } | null;
  assignee: { id: number; name: string; email: string } | null;
  company: { id: number; name: string; domain: string } | null;
  sla_remaining: string;
  sla_expired: boolean;
  sla_deadline: string;
  created_at: string;
  updated_at: string;
  can_reopen_until?: string | null;
  nps_score?: number | null;
  nps_comment?: string | null;
  jira_key?: string | null;
  parent?: { id: string; title: string; status: string } | null;
  children?: Array<{ id: string; title: string; status: string; requester: { name: string; email: string }; created_at: string }>;
}

interface Comment {
  id: number;
  content: string;
  is_internal: boolean;
  image_url?: string | null;
  created_at: string;
  source?: string;
  author_display_name?: string | null;
  author: { id: number; name: string; email: string; role: string; avatar_url?: string | null } | null;
}

interface HistoryItem {
  id: number;
  field_name: string;
  old_value: string | null;
  new_value: string;
  change_type: string;
  note?: string | null;
  created_at: string;
  changed_by: { id: number; name: string; email: string } | null;
}

const InternalTicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [internalUsers, setInternalUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [isLoadingComment, setIsLoadingComment] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [updateData, setUpdateData] = useState({
    status: "",
    priority: "",
    assignee_id: "",
    category: "",
    tracking_status: "",
    queue_level: ""
  });
  const [transferData, setTransferData] = useState({
    assignee_id: "",
    reason: "",
    new_queue: ""
  });
  const [transferMode, setTransferMode] = useState<"technician" | "queue">("technician");
  const [reopenReason, setReopenReason] = useState("");
  const [closureReason, setClosureReason] = useState("");
  const [canReopen, setCanReopen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [quickAssignId, setQuickAssignId] = useState<string>("");
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);

  useEffect(() => {
    const userData = getUser();
    if (!userData) {
      navigate("/login");
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== "internal" && parsedUser.role !== "analyst" && parsedUser.role !== "admin") {
      navigate("/chamados");
      return;
    }

    setCurrentUser(parsedUser);
    fetchTicketData();
  }, [id, navigate]);

  const buildTicketUrl = (path: string) => {
    if (
      currentUser?.role === "admin" &&
      !currentUser?.company_id &&
      ticket?.company?.id
    ) {
      const separator = path.includes("?") ? "&" : "?";
      return `${path}${separator}company_id=${ticket.company.id}`;
    }
    return path;
  };

  const fetchTicketData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const ticketQuery =
        currentUser?.role === "admin" &&
        !currentUser?.company_id &&
        ticket?.company?.id
          ? `?company_id=${ticket.company.id}`
          : "";

      // Buscar ticket
      const ticketResponse = await fetch(getApiEndpoint(`/api/tickets/${id}${ticketQuery}`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (ticketResponse.ok) {
        const ticketData = await ticketResponse.json();
        setTicket(ticketData.ticket);
        setComments(ticketData.comments || []);
        setUpdateData({
          status: ticketData.ticket.status,
          priority: ticketData.ticket.priority,
          assignee_id: ticketData.ticket.assignee?.id?.toString() || "",
          category: ticketData.ticket.category || "",
          tracking_status: ticketData.ticket.tracking_status || "monitoring",
          queue_level: ticketData.ticket.queue_level || "N1"
        });
        
        // Verificar se pode reabrir
        if (ticketData.ticket.can_reopen_until) {
          const now = new Date();
          const reopenUntil = new Date(ticketData.ticket.can_reopen_until);
          setCanReopen(now <= reopenUntil && (ticketData.ticket.status === 'closed' || ticketData.ticket.status === 'resolved'));
        }
        await fetchInternalUsers(ticketData.ticket.company?.id);
      }

      // Buscar histórico
      const historyResponse = await fetch(getApiEndpoint(`/api/tickets/${id}/history${ticketQuery}`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setHistory(historyData.history || []);
      }
    } catch (error) {
      toast({
        title: t.internalTicketDetails.error,
        description: t.internalTicketDetails.loadError,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInternalUsers = async (companyId?: number) => {
    try {
      const token = getToken();
      const query = companyId ? `?company_id=${companyId}` : "";
      const response = await fetch(getApiEndpoint(`/api/tickets/internal/users${query}`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setInternalUsers(data.users || []);
      }
    } catch (error) {
    }
  };

  const setImageFromFile = (file: File) => {
    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t.ticketDetails.fileTooBig,
        description: t.ticketDetails.fileTooBigDesc,
        variant: "destructive",
      });
      return;
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast({
        title: t.ticketDetails.invalidFileType,
        description: t.ticketDetails.invalidFileTypeDesc,
        variant: "destructive",
      });
      return;
    }

    setSelectedImage(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFromFile(file);
  };

  const handlePasteImage = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type.startsWith('image/'));
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (!file) return;
    e.preventDefault();
    setImageFromFile(file);
    toast({
      title: t.internalTicketDetails.printAttached,
      description: t.internalTicketDetails.printAttachedDesc,
    });
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    // Resetar input de arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddComment = async () => {
    if ((!newComment.trim() && !selectedImage) || !ticket) {
      toast({
        title: t.ticketDetails.attention,
        description: t.ticketDetails.addCommentOrImage,
        variant: "destructive",
      });
      return;
    }

    setIsLoadingComment(true);
    try {
      const token = getToken();
      
      if (!token) {
        throw new Error("Token de autenticação não encontrado. Por favor, faça login novamente.");
      }
      
      const formData = new FormData();
      formData.append('content', newComment || '');
      formData.append('is_internal', 'false');
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}/comments`)), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // Não definir Content-Type, o browser define automaticamente com boundary para FormData
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setComments([...comments, data.comment]);
        setNewComment("");
        setSelectedImage(null);
        setImagePreview(null);
        // Resetar input de arquivo
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        toast({
          title: t.internalTicketDetails.commentAdded,
          description: t.ticketDetails.commentAddedDesc,
        });
        fetchTicketData();
      } else {
        let errorMessage = "Erro ao adicionar comentário";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Se não conseguir fazer parse do JSON, usar mensagem genérica baseada no status
          if (response.status === 401) {
            errorMessage = "Sessão expirada. Por favor, faça login novamente.";
          } else if (response.status === 403) {
            errorMessage = "Você não tem permissão para realizar esta ação.";
          } else if (response.status === 400) {
            errorMessage = "Dados inválidos. Verifique os campos e tente novamente.";
          }
        }
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Erro ao adicionar comentário:", error);
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsLoadingComment(false);
    }
  };

  const handleUpdateTicket = async () => {
    if (!ticket) return;

    setIsUpdating(true);
    try {
      const token = getToken();
      const updates: any = {};

      // Verificar se houve mudanças nos campos normais
      if (updateData.status && updateData.status !== ticket.status) {
        updates.status = updateData.status;
      }
      if (updateData.priority && updateData.priority !== ticket.priority) {
        updates.priority = updateData.priority;
      }
      if (updateData.category && updateData.category !== ticket.category) {
        updates.category = updateData.category;
      }
      if (updateData.tracking_status && updateData.tracking_status !== ticket.tracking_status) {
        updates.tracking_status = updateData.tracking_status;
      }
      if (updateData.queue_level && updateData.queue_level !== ticket.queue_level) {
        updates.queue_level = updateData.queue_level;
      }

      // Tratar atribuição/reatribuição
      const currentAssigneeId = ticket.assignee?.id?.toString() || "";
      const newAssigneeId = updateData.assignee_id || "";

      // Se mudou o responsável
      if (currentAssigneeId !== newAssigneeId) {
        // Se está removendo a atribuição (string vazia)
        if (!newAssigneeId) {
          // Para remover atribuição, usar PATCH com assignee_id null
          updates.assignee_id = null;
        } else {
          // Se está atribuindo/reatribuindo, usar endpoint de transferência
          const transferResponse = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}/transfer`)), {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              assignee_id: parseInt(newAssigneeId),
              reason: "Atribuição/Reatribuição pelo admin"
            }),
          });

          if (!transferResponse.ok) {
            throw new Error("Erro ao atribuir chamado");
          }

          // Se havia outras atualizações além da atribuição, fazer um PATCH separado
          if (Object.keys(updates).length > 0) {
            const patchResponse = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}`)), {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(updates),
            });

            if (!patchResponse.ok) {
              throw new Error("Erro ao atualizar chamado");
            }
          }

          toast({
            title: t.internalTicketDetails.ticketUpdated,
            description: t.internalTicketDetails.ticketUpdated,
          });
          fetchTicketData();
          setIsUpdating(false);
          return;
        }
      }

      // Se não há mudanças, não fazer requisição
      if (Object.keys(updates).length === 0) {
        toast({
          title: t.internalTicketDetails.noChangesToSave,
          description: t.internalTicketDetails.noChangesToSaveDesc,
        });
        setIsUpdating(false);
        return;
      }

      // Atualizar campos normais (sem mudança de atribuição)
      const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}`)), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        toast({
          title: t.internalTicketDetails.ticketUpdated,
          description: t.internalTicketDetails.ticketUpdated,
        });
        fetchTicketData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t.internalTicketDetails.updateError);
      }
    } catch (error: any) {
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTransferTicket = async () => {
    if (!ticket) return;

    setIsUpdating(true);
    try {
      const token = getToken();

      // Montar payload conforme a opção escolhida
      let payload: any = { reason: transferData.reason };

      if (transferMode === "technician") {
        if (!transferData.assignee_id) {
          throw new Error("Selecione o técnico de destino.");
        }
        payload.assignee_id = parseInt(transferData.assignee_id);
        if (transferData.new_queue) payload.new_queue = transferData.new_queue;
      } else {
        // transferMode === "queue" (mantém responsável)
        if (!transferData.new_queue) {
          throw new Error("Selecione a fila de destino.");
        }
        if (!ticket.assignee?.id) {
          throw new Error("Este chamado não possui responsável. Selecione um técnico para transferir.");
        }
        payload.assignee_id = ticket.assignee.id; // manter o mesmo responsável
        payload.new_queue = transferData.new_queue;
        if (!payload.reason) {
          payload.reason = "Mudança de fila (mantém responsável)";
        }
      }

      const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}/transfer`)), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: t.internalTicketDetails.transferSuccess,
          description: t.internalTicketDetails.transferSuccess,
        });
        setTransferData({ assignee_id: "", reason: "", new_queue: "" });
        setTransferMode("technician");
        fetchTicketData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao transferir chamado");
      }
    } catch (error: any) {
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleQuickAssign = async () => {
    if (!ticket) {
      toast({
        title: "Erro",
        description: "Chamado não encontrado.",
        variant: "destructive",
      });
      return;
    }

    // Se não selecionou nada e também não quer remover (não há atribuição atual)
    if (!quickAssignId && !ticket.assignee) {
      toast({
        title: t.internalTicketDetails.error,
        description: t.internalTicketDetails.selectAssignee,
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const token = getToken();
      
      // Se está removendo atribuição (valor especial)
      if (quickAssignId === "__REMOVE_ASSIGNMENT__" && ticket.assignee) {
        const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}`)), {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ assignee_id: null }),
        });

        if (response.ok) {
          toast({
            title: t.internalTicketDetails.assignmentRemoved,
            description: t.internalTicketDetails.assignmentRemovedDesc,
          });
          setAssignmentDialogOpen(false);
          setQuickAssignId("");
          fetchTicketData();
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Erro ao remover atribuição");
        }
      } else if (quickAssignId && quickAssignId !== "" && quickAssignId !== "__REMOVE_ASSIGNMENT__") {
        // Atribuir ou reatribuir
        const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}/transfer`)), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assignee_id: parseInt(quickAssignId),
            reason: "Atribuição rápida"
          }),
        });

        if (response.ok) {
          toast({
            title: t.internalTicketDetails.assignSuccess,
            description: t.internalTicketDetails.assignSuccess,
          });
          setAssignmentDialogOpen(false);
          setQuickAssignId("");
          fetchTicketData();
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Erro ao atribuir chamado");
        }
      } else {
        toast({
          title: t.internalTicketDetails.noChangesToSave,
          description: t.internalTicketDetails.noChangesToSaveDesc,
        });
      }
    } catch (error: any) {
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReopenTicket = async () => {
    if (!ticket) return;

    setIsUpdating(true);
    try {
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}/reopen`)), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: reopenReason }),
      });

      if (response.ok) {
        toast({
          title: t.internalTicketDetails.reopenSuccess,
          description: t.internalTicketDetails.reopenSuccess,
        });
        setReopenReason("");
        fetchTicketData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao reabrir chamado");
      }
    } catch (error: any) {
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResolveTicket = async () => {
    if (!ticket) return;
    if (!closureReason.trim()) {
      toast({
        title: t.internalTicketDetails.reasonRequired,
        description: t.internalTicketDetails.reasonRequiredDesc,
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}`)), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "resolved", closure_reason: closureReason.trim() }),
      });

      if (response.ok) {
        toast({
          title: t.internalTicketDetails.resolveSuccess,
          description: t.internalTicketDetails.resolveSuccess,
        });
        setClosureReason("");
        fetchTicketData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Erro ao resolver chamado");
      }
    } catch (error: any) {
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!ticket) return;
    if (!closureReason.trim()) {
      toast({
        title: t.internalTicketDetails.reasonRequired,
        description: t.internalTicketDetails.reasonRequiredDesc,
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}`)), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "closed", closure_reason: closureReason.trim() }),
      });

      if (response.ok) {
        toast({
          title: t.internalTicketDetails.closeSuccess,
          description: t.internalTicketDetails.closeSuccess,
        });
        setClosureReason("");
        fetchTicketData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Erro ao fechar chamado");
      }
    } catch (error: any) {
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUnlinkTicket = async () => {
    if (!ticket) return;

    setIsUpdating(true);
    try {
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(buildTicketUrl(`/api/tickets/${ticket.id}/link`)), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ parent_id: null }),
      });

      if (response.ok) {
        toast({
          title: t.internalTicketDetails.unlinkSuccess,
          description: t.internalTicketDetails.unlinkSuccess,
        });
        fetchTicketData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t.internalTicketDetails.updateError);
      }
    } catch (error: any) {
      toast({
        title: t.internalTicketDetails.error,
        description: error.message || t.internalTicketDetails.updateError,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-yellow-100 text-yellow-800";
      case "waiting_response": return "bg-orange-100 text-orange-800";
      case "resolved": return "bg-green-100 text-green-800";
      case "closed": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "open": return t.internalTicketDetails.statusOpen;
      case "in_progress": return t.internalTicketDetails.statusInProgress;
      case "waiting_response": return t.internalTicketDetails.statusWaiting;
      case "resolved": return t.internalTicketDetails.statusResolved;
      case "closed": return t.internalTicketDetails.statusClosed;
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-red-100 text-red-800";
      case "high": return "bg-orange-100 text-orange-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case "critical": return t.internalTicketDetails.priorityCritical;
      case "high": return t.internalTicketDetails.priorityHigh;
      case "medium": return t.internalTicketDetails.priorityMedium;
      case "low": return t.internalTicketDetails.priorityLow;
      default: return priority;
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "";
    return new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  const getLevelLabel = (level: string) => {
    switch (level) {
      case "critical": return t.internalTicketDetails.priorityCritical;
      case "high": return t.internalTicketDetails.priorityHigh;
      case "medium": return t.internalTicketDetails.priorityMedium;
      case "low": return t.internalTicketDetails.priorityLow;
      default: return level;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "incident": return <AlertTriangleIcon className="h-5 w-5 text-red-600" />;
      case "request": return <CheckCircleIcon className="h-5 w-5 text-blue-600" />;
      case "problem": return <TrendingUpIcon className="h-5 w-5 text-purple-600" />;
      case "change": return <ClockIcon className="h-5 w-5 text-green-600" />;
      default: return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "incident": return t.internalTicketDetails.typeIncident;
      case "request": return t.internalTicketDetails.typeRequest;
      case "problem": return t.internalTicketDetails.typeProblem;
      case "change": return t.internalTicketDetails.typeChange;
      default: return type;
    }
  };

  const getChangeTypeLabel = (changeType: string) => {
    switch (changeType) {
      case "created": return t.internalTicketDetails.changeTypeCreated;
      case "status_change": return t.internalTicketDetails.changeTypeStatusChange;
      case "priority_change": return t.internalTicketDetails.changeTypePriorityChange;
      case "category_change": return t.internalTicketDetails.changeTypeCategoryChange;
      case "tracking_status_change": return t.internalTicketDetails.changeTypeTrackingStatusChange;
      case "queue_change": return t.internalTicketDetails.changeTypeQueueChange;
      case "assignment": return t.internalTicketDetails.changeTypeAssignment;
      case "transfer": return t.internalTicketDetails.changeTypeTransfer;
      case "link": return t.internalTicketDetails.changeTypeLink;
      case "unlink": return t.internalTicketDetails.changeTypeUnlink;
      case "reopen": return t.internalTicketDetails.changeTypeReopen;
      case "system_close": return t.internalTicketDetails.changeTypeSystemClose;
      case "comment_added": return t.internalTicketDetails.changeTypeCommentAdded;
      case "attachment_added": return t.internalTicketDetails.changeTypeAttachmentAdded;
      case "nps_rated": return t.internalTicketDetails.changeTypeNpsRated;
      case "field_update": return t.internalTicketDetails.changeTypeFieldUpdate;
      default: return changeType;
    }
  };

  const formatFieldLabel = (fieldName: string) => {
    switch (fieldName) {
      case "status": return t.internalTicketDetails.status;
      case "priority": return t.internalTicketDetails.priorityLabel;
      case "category": return t.internalTicketDetails.category;
      case "assignee_id": return t.internalTicketDetails.assigneeLabel;
      case "tracking_status": return t.internalTicketDetails.trackingStatus;
      case "queue_level": return t.internalTicketDetails.queue;
      case "parent_id": return t.internalTicketDetails.parentIdLabel;
      case "comment": return t.internalTicketDetails.commentLabel;
      case "attachments": return t.internalTicketDetails.attachmentsLabel;
      case "nps_score": return t.internalTicketDetails.npsScoreLabel;
      default: return fieldName;
    }
  };

  const formatValue = (fieldName: string, value: string | null) => {
    if (value === null || value === undefined) return "";
    if (fieldName === "status") {
      return getStatusLabel(value);
    }
    if (fieldName === "priority") {
      return getPriorityLabel(value);
    }
    if (fieldName === "queue_level") {
      switch (value) {
        case "N1": return t.internalTicketDetails.queueN1;
        case "N2": return t.internalTicketDetails.queueN2;
        case "N3": return t.internalTicketDetails.queueN3;
        default: return value;
      }
    }
    if (fieldName === "tracking_status") {
      switch (value) {
        case "monitoring": return t.internalTicketDetails.trackingMonitoring;
        case "waiting": return t.internalTicketDetails.trackingWaiting;
        case "paused": return t.internalTicketDetails.trackingPaused;
        case "resolved": return t.internalTicketDetails.trackingResolved;
        case "escalated": return t.internalTicketDetails.trackingEscalated;
        default: return value;
      }
    }
    if (fieldName === "assignee_id") {
      const idValue = parseInt(String(value), 10);
      const matchedUser = internalUsers?.find((u) => u && u.id === idValue);
      if (matchedUser?.name) return matchedUser.name;
      if (ticket?.assignee?.id === idValue && ticket.assignee?.name) return ticket.assignee.name;
      return `ID ${value}`;
    }
    if (fieldName === "parent_id") {
      return value ? `#${value}` : t.internalTicketDetails.noLink;
    }
    if (fieldName === "attachments") {
      return t.internalTicketDetails.attachmentCount(parseInt(String(value), 10) || 0);
    }
    if (fieldName === "comment") {
      return value === "added" ? t.internalTicketDetails.changeTypeCommentAdded : value;
    }
    if (fieldName === "nps_score") {
      return `${value}/10`;
    }
    return value;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">{t.internalTicketDetails.loading}</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t.internalTicketDetails.notFound}</h2>
          <Link to="/atendimento">
            <Button>{t.internalTicketDetails.backToPanel}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <Link to="/atendimento" className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            {t.internalTicketDetails.backToPanel}
          </Link>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {getTypeIcon(ticket.type)}
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">#{ticket.id}</h1>
                <Badge className={getStatusColor(ticket.status)}>
                  {getStatusLabel(ticket.status)}
                </Badge>
                <Badge className={getPriorityColor(ticket.priority)}>
                  {getPriorityLabel(ticket.priority)}
                </Badge>
                {ticket.sla_expired && (
                  <Badge className="bg-red-100 text-red-800">{t.internalTicketDetails.slaExpired}</Badge>
                )}
              </div>
              <h2 className="text-xl text-gray-700 dark:text-gray-200 mb-2">{ticket.title}</h2>
              <p className="text-gray-600 dark:text-gray-300">
                {t.ticketDetails.createdAt} {formatDateTime(ticket.created_at)} • 
                {t.ticketDetails.updatedAt} {formatDateTime(ticket.updated_at)}
              </p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details">{t.internalTicketDetails.details}</TabsTrigger>
            <TabsTrigger value="comments">{t.internalTicketDetails.communication} ({comments.length})</TabsTrigger>
            <TabsTrigger value="history">{t.internalTicketDetails.history} ({history.length})</TabsTrigger>
            <TabsTrigger value="actions">{t.internalTicketDetails.actions}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Conteúdo Principal */}
              <div className="lg:col-span-2 space-y-6">
                {/* Descrição */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t.internalTicketDetails.description}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{ticket.description}</p>
                  </CardContent>
                </Card>

                {/* Informações da Empresa */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BuildingIcon className="h-5 w-5 mr-2" />
                      {t.internalTicketDetails.companyInfo}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.company}</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{ticket.company?.name || t.internalTicketDetails.notInformed}</p>
                      {ticket.company && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{ticket.company.domain}</p>
                      )}
                    </div>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.requester}</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{ticket.requester?.name || t.internalTicketDetails.notInformed}</p>
                      {ticket.requester && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{ticket.requester.email}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar de Informações */}
              <div className="space-y-6">
                {/* SLA */}
                <Card className={ticket.sla_expired ? "border-red-300" : ""}>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2" />
                      {t.internalTicketDetails.sla}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.timeRemaining}</p>
                        <p className={`text-lg font-semibold ${ticket.sla_expired ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                          {ticket.sla_expired ? t.internalTicketDetails.slaOverdue : ticket.sla_remaining}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.deadline}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                          {new Date(ticket.sla_deadline).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Detalhes do Chamado */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t.internalTicketDetails.details}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.type}</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{getTypeLabel(ticket.type)}</p>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.category}</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{ticket.category}</p>
                      {ticket.subcategory && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">{ticket.subcategory}</p>
                      )}
                    </div>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.impact}</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{getLevelLabel(ticket.impact)}</p>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.urgency}</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{getLevelLabel(ticket.urgency)}</p>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.priority}{t.internalTicketDetails.priorityWithSlaSuffix}</p>
                      <Badge className={getPriorityColor(ticket.priority)}>
                        {getPriorityLabel(ticket.priority)}
                      </Badge>
                      {ticket.customer_priority && ticket.customer_priority !== ticket.priority && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          {t.internalTicketDetails.customerPriority}{" "}
                          <span className="font-medium">{getPriorityLabel(ticket.customer_priority)}</span>
                        </p>
                      )}
                      {ticket.customer_priority && ticket.customer_priority === ticket.priority && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          {t.internalTicketDetails.customerPriority}{" "}
                          <span className="font-medium">{getPriorityLabel(ticket.customer_priority)}</span>
                        </p>
                      )}
                    </div>
                    <Separator />
                    {(ticket.contact_phone || ticket.preferred_contact_time) && (
                        <>
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.contact}</p>
                          {ticket.contact_phone && (
                            <p className="text-sm text-gray-900 dark:text-gray-100">📞 {ticket.contact_phone}</p>
                          )}
                          {ticket.preferred_contact_time && (
                            <p className="text-sm text-gray-700 dark:text-gray-200 mt-1">
                              ⏰ {ticket.preferred_contact_time}
                            </p>
                          )}
                        </div>
                        <Separator />
                      </>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.internalTicketDetails.assigneeLabel}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (ticket && ticket.assignee && ticket.assignee.id) {
                              setQuickAssignId(ticket.assignee.id.toString());
                            } else {
                              setQuickAssignId("");
                            }
                            setAssignmentDialogOpen(true);
                          }}
                          className="h-7 text-xs"
                        >
                          {ticket.assignee ? `🔄 ${t.internalTicketDetails.reassign}` : `➕ ${t.internalTicketDetails.assign}`}
                        </Button>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {ticket.assignee ? ticket.assignee.name : t.internalTicketDetails.notAssigned}
                      </p>
                      {ticket.assignee && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{ticket.assignee.email}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Chamados Vinculados */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <GitBranchIcon className="h-5 w-5 mr-2" />
                      {t.internalTicketDetails.relatedTickets}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Chamado Pai */}
                    {ticket.parent && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-xs font-medium text-blue-900 mb-1">{t.internalTicketDetails.mainTicket}</p>
                            <p className="text-sm font-semibold text-blue-900">{ticket.parent.id}</p>
                            <p className="text-xs text-blue-700 truncate">{ticket.parent.title}</p>
                            <Badge className={`mt-1 ${ticket.parent.status === 'resolved' || ticket.parent.status === 'closed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                              {getStatusLabel(ticket.parent.status)}
                            </Badge>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnlinkTicket()}
                            className="ml-2"
                          >
                            <Link2OffIcon className="h-3 w-3 mr-1" />
                            {t.internalTicketDetails.unlink}
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/internal/chamado/${ticket.parent?.id}`)}
                          className="mt-2 w-full text-xs"
                        >
                          {t.internalTicketDetails.viewMain} →
                        </Button>
                      </div>
                    )}

                    {/* Chamados Filhos */}
                    {ticket.children && ticket.children.length > 0 && (
                      <div className="space-y-2">
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                            {t.internalTicketDetails.peopleReportedSame}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t.internalTicketDetails.peopleReportedCount(ticket.children.length)}
                          </p>
                        </div>
                        {ticket.children.map((child) => (
                          <div key={child.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{child.id}</p>
                                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{child.title}</p>
                              </div>
                              <Badge className={`flex-shrink-0 ${child.status === 'resolved' || child.status === 'closed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {getStatusLabel(child.status)}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                              <span className="flex items-center gap-1">
                                <UserIcon className="h-3 w-3" />
                                {child.requester?.name || child.requester?.email || 'Usuário'}
                              </span>
                              <span>{new Date(child.created_at).toLocaleDateString('pt-BR')}</span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/atendimento/chamado/${child.id}`);
                              }}
                              className="w-full text-xs"
                            >
                              {t.internalTicketDetails.details} →
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {!ticket.parent && (!ticket.children || ticket.children.length === 0) && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                        {t.internalTicketDetails.noLinkedTickets}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="comments" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center text-gray-900 dark:text-gray-100">
                      <MessageSquareIcon className="h-5 w-5 mr-2" />
                      {t.internalTicketDetails.communication} ({comments.length})
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-300 mt-1">
                      {t.internalTicketDetails.communicationDesc}
                      {ticket?.jira_key && (
                        <span className="block mt-1 text-blue-600 dark:text-blue-400 text-xs">
                          Sincronizado com Jira. Comentários enviados aqui aparecem no Jira e vice-versa. Use &quot;Atualizar&quot; para buscar novos do Jira.
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fetchTicketData()}
                    className="shrink-0"
                  >
                    <RefreshCwIcon className="h-4 w-4 mr-1" />
                    Atualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <MessageSquareIcon className="h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-500" />
                    <p>{t.internalTicketDetails.noMessages}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="relative">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <Avatar className="h-10 w-10 border-2 border-gray-200 dark:border-gray-600">
                              <AvatarImage
                                src={comment.author?.avatar_url ? (comment.author.avatar_url.startsWith("http") ? comment.author.avatar_url : getApiEndpoint(comment.author.avatar_url)) : undefined}
                                alt={comment.author?.name}
                              />
                              <AvatarFallback className="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-sm font-medium">
                                {(comment.author?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {comment.author?.name || comment.author_display_name || t.ticketDetails.user}
                              </p>
                              {comment.source === 'jira' && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                  Jira
                                </span>
                              )}
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(comment.created_at).toLocaleString('pt-BR')}
                              </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                              {comment.content && (
                                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap mb-2">{comment.content}</p>
                              )}
                              {comment.image_url && (
                                <div className="mt-2">
                                  <img 
                                    src={comment.image_url?.startsWith('http') ? comment.image_url : `${API_URL || ''}${comment.image_url}`}
                                    alt={t.ticketDetails.attachedImage}
                                    className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer"
                                    onClick={() => window.open(comment.image_url?.startsWith('http') ? comment.image_url : `${API_URL || ''}${comment.image_url}`, '_blank')}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Adicionar Comentário */}
                {ticket.status !== "closed" && (
                  <div className="border-t pt-4">
                    <div className="space-y-3">
                      <Textarea
                        placeholder={t.internalTicketDetails.addCommentPlaceholder}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value.slice(0, 1000))}
                        onPaste={handlePasteImage}
                        rows={3}
                        maxLength={1000}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                        {newComment.length}/1000
                      </p>
                      
                      {/* Upload de Imagem */}
                      <div className="space-y-2">
                          <Label htmlFor="image-upload" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t.internalTicketDetails.attachImagePaste}
                          </Label>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="image-upload"
                            className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <ImageIcon className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-300" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">{t.internalTicketDetails.selectImage}</span>
                            <input
                              ref={fileInputRef}
                              id="image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageSelect}
                              className="hidden"
                            />
                          </label>
                          {selectedImage && (
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          )}
                        </div>
                        
                        {/* Preview da Imagem */}
                        {imagePreview && (
                          <div className="relative inline-block mt-2">
                            <div className="relative">
                              <img
                                src={imagePreview}
                                alt={t.internalTicketDetails.attachedImagePreview}
                                className="max-w-full max-h-64 h-auto rounded-lg border-2 border-gray-300 dark:border-gray-600 shadow-sm object-contain"
                              />
                              <button
                                type="button"
                                onClick={removeImage}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg"
                                aria-label={t.internalTicketDetails.removeImage}
                              >
                                <XIcon className="h-4 w-4" />
                              </button>
                            </div>
                            {selectedImage && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {selectedImage.name} ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <Button 
                        onClick={handleAddComment} 
                        disabled={isLoadingComment || (!newComment.trim() && !selectedImage)}
                        className="w-full"
                      >
                        {isLoadingComment ? t.internalTicketDetails.sending : t.internalTicketDetails.addComment}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-gray-900 dark:text-gray-100">
                  <HistoryIcon className="h-5 w-5 mr-2" />
                  {t.internalTicketDetails.history} ({history.length})
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-300">
                  {t.internalTicketDetails.historyDesc}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <HistoryIcon className="h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-500" />
                    <p>{t.internalTicketDetails.noChanges}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((item, index) => (
                      <div key={item.id} className="relative">
                        {index < history.length - 1 && (
                          <div className="absolute left-5 top-8 w-px h-6 bg-gray-200 dark:bg-gray-600"></div>
                        )}
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                              <SettingsIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {getChangeTypeLabel(item.change_type)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(item.created_at).toLocaleString('pt-BR')}
                              </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                              <p className="text-xs text-gray-700 dark:text-gray-200">
                                <span className="font-medium">{formatFieldLabel(item.field_name)}:</span>{" "}
                                {item.old_value
                                  ? `${formatValue(item.field_name, item.old_value)} → ${formatValue(item.field_name, item.new_value)}`
                                  : formatValue(item.field_name, item.new_value)}
                              </p>
                              {(item.note || (item.change_type === "created" && ticket?.description)) && (
                                <p className="text-xs text-gray-600 dark:text-gray-300 mt-2">
                                  <span className="font-medium">{t.internalTicketDetails.reasonLabel}</span>{" "}
                                  {item.change_type === "created" && ticket?.description ? ticket.description : item.note}
                                </p>
                              )}
                              {item.changed_by && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                  {t.internalTicketDetails.changedByLabel} {item.changed_by.name}{item.changed_by.email ? ` (${item.changed_by.email})` : ""}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="actions" className="space-y-6">
            {!ticket ? (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    {t.internalTicketDetails.loadingTicketInfo}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-gray-900 dark:text-gray-100">
                  <SettingsIcon className="h-5 w-5 mr-2" />
                  {t.internalTicketDetails.manageTicket}
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-300">
                  {t.internalTicketDetails.manageDesc}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="status">{t.internalTicketDetails.status}</Label>
                  <Select value={updateData.status || ""} onValueChange={(value) => setUpdateData({...updateData, status: value})}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">{t.internalTicketDetails.statusOpen}</SelectItem>
                      <SelectItem value="in_progress">{t.internalTicketDetails.statusInProgress}</SelectItem>
                      <SelectItem value="waiting_response">{t.internalTicketDetails.statusWaiting}</SelectItem>
                      <SelectItem value="resolved">{t.internalTicketDetails.statusResolved}</SelectItem>
                      <SelectItem value="closed">{t.internalTicketDetails.statusClosed}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">{t.internalTicketDetails.priorityLabel}</Label>
                  <Select value={updateData.priority || ""} onValueChange={(value) => setUpdateData({...updateData, priority: value})}>
                    <SelectTrigger id="priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">{t.internalTicketDetails.priorityCritical}</SelectItem>
                      <SelectItem value="high">{t.internalTicketDetails.priorityHigh}</SelectItem>
                      <SelectItem value="medium">{t.internalTicketDetails.priorityMedium}</SelectItem>
                      <SelectItem value="low">{t.internalTicketDetails.priorityLow}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">{t.internalTicketDetails.category}</Label>
                  <Select value={updateData.category || ""} onValueChange={(value) => setUpdateData({...updateData, category: value})}>
                    <SelectTrigger id="category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Infraestrutura">{t.internalTicketDetails.categoryInfra}</SelectItem>
                      <SelectItem value="Hardware">{t.internalTicketDetails.categoryHardware}</SelectItem>
                      <SelectItem value="Software">{t.internalTicketDetails.categorySoftware}</SelectItem>
                      <SelectItem value="Acesso e Permissões">{t.internalTicketDetails.categoryAccess}</SelectItem>
                      <SelectItem value="Performance">{t.internalTicketDetails.categoryPerformance}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tracking_status">{t.internalTicketDetails.trackingStatus}</Label>
                  <Select value={updateData.tracking_status || ""} onValueChange={(value) => setUpdateData({...updateData, tracking_status: value})}>
                    <SelectTrigger id="tracking_status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monitoring">{t.internalTicketDetails.trackingMonitoring}</SelectItem>
                      <SelectItem value="waiting">{t.internalTicketDetails.trackingWaiting}</SelectItem>
                      <SelectItem value="resolved">{t.internalTicketDetails.trackingResolved}</SelectItem>
                      <SelectItem value="escalated">{t.internalTicketDetails.trackingEscalated}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="queue_level">{t.internalTicketDetails.queue}</Label>
                  <Select value={updateData.queue_level || ticket.queue_level || "N1"} onValueChange={(value) => setUpdateData({...updateData, queue_level: value})}>
                    <SelectTrigger id="queue_level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="N1">🟢 {t.internalTicketDetails.queueN1}</SelectItem>
                      <SelectItem value="N2">🟡 {t.internalTicketDetails.queueN2}</SelectItem>
                      <SelectItem value="N3">🔴 {t.internalTicketDetails.queueN3}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t.internalTicketDetails.queueChangeHint}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="assignee">{t.internalTicketDetails.assigneeLabel}</Label>
                  <Select value={updateData.assignee_id || undefined} onValueChange={(value) => {
                    if (value === "__REMOVE_ASSIGNMENT__") {
                      setUpdateData({...updateData, assignee_id: ""});
                    } else {
                      setUpdateData({...updateData, assignee_id: value});
                    }
                  }}>
                    <SelectTrigger id="assignee">
                      <SelectValue placeholder={t.internalTicketDetails.selectAssignee} />
                    </SelectTrigger>
                    <SelectContent>
                      {updateData.assignee_id && (
                        <SelectItem value="__REMOVE_ASSIGNMENT__">{t.internalTicketDetails.removeAssignment}</SelectItem>
                      )}
                      {currentUser && currentUser.id && (
                        <SelectItem value={currentUser.id.toString()}>
                          {currentUser.name || t.internalTicketDetails.meShort} ({t.internalTicketDetails.meShort}) - {currentUser.role === "admin" ? t.internalTicketDetails.roleAdmin : currentUser.role === "analyst" ? t.internalTicketDetails.roleAnalyst : t.internalTicketDetails.roleAttendant}
                        </SelectItem>
                      )}
                      {internalUsers && Array.isArray(internalUsers) && internalUsers
                        .filter(user => user && user.id && (!currentUser || !currentUser.id || user.id !== currentUser.id))
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id.toString()}>
                            {user.name || t.internalTicketDetails.noName} ({user.email || t.internalTicketDetails.noEmail}) - {user.role === "admin" ? t.internalTicketDetails.roleAdmin : user.role === "analyst" ? t.internalTicketDetails.roleAnalyst : t.internalTicketDetails.roleAttendant}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleUpdateTicket} 
                  disabled={isUpdating}
                  className="w-full"
                >
                  {isUpdating ? t.internalTicketDetails.saving : t.internalTicketDetails.saveChanges}
                </Button>
              </CardContent>
            </Card>

            {/* Ações Rápidas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-gray-900 dark:text-gray-100">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {t.internalTicketDetails.quickActions}
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-300">
                  {t.internalTicketDetails.quickActionsDesc}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {ticket.status !== "closed" && (
                  <div className="space-y-2">
                    <Label htmlFor="closure_reason">{t.internalTicketDetails.closureReason}</Label>
                    <Textarea
                      id="closure_reason"
                      placeholder={t.internalTicketDetails.closureReasonPlaceholder}
                      value={closureReason}
                      onChange={(e) => setClosureReason(e.target.value)}
                      rows={3}
                    />
<p className="text-xs text-gray-500 dark:text-gray-400">
                    {t.internalTicketDetails.closureReasonHint}
                    </p>
                  </div>
                )}
                {/* Botão Resolver - aparece quando não está resolvido nem fechado */}
                {(ticket.status !== "resolved" && ticket.status !== "closed") && (
                  <Button
                    onClick={handleResolveTicket}
                    disabled={isUpdating || !closureReason.trim()}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    {t.internalTicketDetails.resolveTicket}
                  </Button>
                )}

                {/* Botão Fechar - aparece quando está resolvido mas não fechado */}
                {ticket.status === "resolved" && (
                  <Button
                    onClick={handleCloseTicket}
                    disabled={isUpdating || !closureReason.trim()}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    <XIcon className="h-4 w-4 mr-2" />
                    {t.internalTicketDetails.closeTicket}
                  </Button>
                )}

                {/* Botão Reabrir - aparece quando está fechado ou resolvido */}
                {(ticket.status === "closed" || ticket.status === "resolved") && canReopen && (
                  <Button
                    onClick={() => {
                      setReopenReason("");
                      setReopenDialogOpen(true);
                    }}
                    disabled={isUpdating}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <AlertTriangleIcon className="h-4 w-4 mr-2" />
                    {t.internalTicketDetails.reopenTicket}
                  </Button>
                )}

                {/* Mensagem quando não há ações disponíveis */}
                {(ticket.status === "closed" && !canReopen) && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                    {t.internalTicketDetails.reopenDeadlineExpired}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transferência de Chamado */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <UserCheckIcon className="h-5 w-5 mr-2" />
                  {t.internalTicketDetails.transferTicket}
                </CardTitle>
                <CardDescription>
                  {t.internalTicketDetails.transferDesc}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.internalTicketDetails.transferOption}</Label>
                  <RadioGroup
                    value={transferMode}
                    onValueChange={(v) => setTransferMode(v as "technician" | "queue")}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="technician" id="transfer-mode-technician" />
                      <Label htmlFor="transfer-mode-technician">{t.internalTicketDetails.transferToTechnician}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="queue" id="transfer-mode-queue" />
                      <Label htmlFor="transfer-mode-queue">{t.internalTicketDetails.transferQueueOnly}</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  {transferMode === "technician" ? (
                    <>
                      <Label htmlFor="transfer_assignee">{t.internalTicketDetails.newAssignee}</Label>
                      <Select
                        value={transferData.assignee_id}
                        onValueChange={(value) => setTransferData({ ...transferData, assignee_id: value })}
                      >
                        <SelectTrigger id="transfer_assignee">
                          <SelectValue placeholder={t.internalTicketDetails.selectTechnician} />
                        </SelectTrigger>
                        <SelectContent>
                          {internalUsers &&
                            Array.isArray(internalUsers) &&
                            internalUsers
                              .filter((u) => u && u.id && u.id.toString() !== (updateData.assignee_id || ""))
                              .map((user) => (
                                <SelectItem key={user.id} value={user.id.toString()}>
                                  {user.name || t.internalTicketDetails.noName} ({user.email || t.internalTicketDetails.noEmail})
                                </SelectItem>
                              ))}
                        </SelectContent>
                      </Select>
                    </>
                  ) : (
                    <>
                      <Label htmlFor="transfer_queue">{t.internalTicketDetails.destinationQueue}</Label>
                      <Select
                        value={transferData.new_queue}
                        onValueChange={(value) => setTransferData({ ...transferData, new_queue: value })}
                      >
                        <SelectTrigger id="transfer_queue">
                          <SelectValue placeholder={t.internalTicketDetails.selectQueue} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="N1">🟢 {t.internalTicketDetails.queueN1}</SelectItem>
                          <SelectItem value="N2">🟡 {t.internalTicketDetails.queueN2}</SelectItem>
                          <SelectItem value="N3">🔴 {t.internalTicketDetails.queueN3}</SelectItem>
                        </SelectContent>
                      </Select>
                      {!ticket.assignee?.id && (
                        <p className="text-xs text-orange-600">
                          {t.internalTicketDetails.noAssigneeQueue}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {transferMode === "technician" && (
                  <div className="space-y-2">
                    <Label htmlFor="transfer_queue_optional">{t.internalTicketDetails.optionalQueue}</Label>
                    <Select
                      value={transferData.new_queue || ""}
                      onValueChange={(value) => setTransferData({ ...transferData, new_queue: value })}
                    >
                      <SelectTrigger id="transfer_queue_optional">
                        <SelectValue placeholder={t.internalTicketDetails.optionalQueuePlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="N1">🟢 {t.internalTicketDetails.queueN1}</SelectItem>
                        <SelectItem value="N2">🟡 {t.internalTicketDetails.queueN2}</SelectItem>
                        <SelectItem value="N3">🔴 {t.internalTicketDetails.queueN3}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t.internalTicketDetails.optionalQueueHint}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="transfer_reason">{t.internalTicketDetails.transferReason}</Label>
                  <Textarea
                    id="transfer_reason"
                    placeholder={t.internalTicketDetails.transferReasonPlaceholder}
                    value={transferData.reason}
                    onChange={(e) => setTransferData({...transferData, reason: e.target.value})}
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleTransferTicket} 
                  disabled={
                    isUpdating ||
                    (transferMode === "technician" ? !transferData.assignee_id : !transferData.new_queue)
                  }
                  className="w-full"
                  variant="outline"
                >
                  {t.internalTicketDetails.transferBtn}
                </Button>
              </CardContent>
            </Card>

            {/* Reabertura de Chamado */}
            {(ticket.status === 'closed' || ticket.status === 'resolved') && canReopen && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertTriangleIcon className="h-5 w-5 mr-2" />
                    {t.internalTicketDetails.reopenTitle}
                  </CardTitle>
                  <CardDescription>
                    {t.internalTicketDetails.reopenDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reopen_reason">{t.internalTicketDetails.reopenReasonLabel}</Label>
                    <Textarea
                      id="reopen_reason"
                      placeholder={t.internalTicketDetails.reopenReasonPlaceholder}
                      value={reopenReason}
                      onChange={(e) => setReopenReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                  {ticket.can_reopen_until && (
                    <p className="text-sm text-gray-600">
                      {t.internalTicketDetails.reopenDeadline} {new Date(ticket.can_reopen_until).toLocaleString()}
                    </p>
                  )}
                  <Button 
                    onClick={handleReopenTicket} 
                    disabled={isUpdating || !reopenReason.trim()}
                    className="w-full"
                    variant="outline"
                  >
                    {t.internalTicketDetails.reopenTicket}
                  </Button>
                </CardContent>
              </Card>
            )}
            </>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Footer />

      {/* Dialog de Atribuição Rápida */}
      {ticket && (
        <Dialog open={assignmentDialogOpen} onOpenChange={(open) => {
          setAssignmentDialogOpen(open);
          if (!open) {
            // Reset ao fechar
            if (ticket.assignee && ticket.assignee.id) {
              setQuickAssignId(ticket.assignee.id.toString());
            } else {
              setQuickAssignId("");
            }
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {ticket.assignee ? t.internalTicketDetails.reassignTicket : t.internalTicketDetails.assignTicket}
              </DialogTitle>
              <DialogDescription>
                {t.internalTicketDetails.selectForTicket} #{ticket.id}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t.internalTicketDetails.ticketTitle}
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-300">{ticket.title || "N/A"}</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t.internalTicketDetails.currentAssignee}
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {ticket.assignee ? ticket.assignee.name : t.internalTicketDetails.notAssigned}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t.internalTicketDetails.assignTo}
                </label>
                <Select value={quickAssignId || undefined} onValueChange={setQuickAssignId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.internalTicketDetails.selectAssignee} />
                  </SelectTrigger>
                <SelectContent>
                  {ticket && ticket.assignee && (
                    <SelectItem value="__REMOVE_ASSIGNMENT__">{t.internalTicketDetails.removeAssignment}</SelectItem>
                  )}
                  {currentUser && currentUser.id && (
                    <SelectItem value={currentUser.id.toString()}>
                      {currentUser.name || t.internalTicketDetails.meShort} ({t.internalTicketDetails.meShort}) - {currentUser.role === "admin" ? t.internalTicketDetails.roleAdmin : currentUser.role === "analyst" ? t.internalTicketDetails.roleAnalyst : t.internalTicketDetails.roleAttendant}
                    </SelectItem>
                  )}
                  {internalUsers && Array.isArray(internalUsers) && internalUsers.length > 0 && internalUsers
                    .filter(user => user && user.id && (!currentUser || !currentUser.id || user.id !== currentUser.id))
                    .map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.name || t.internalTicketDetails.noName} ({user.email || t.internalTicketDetails.noEmail}) - {user.role === "admin" ? t.internalTicketDetails.roleAdmin : user.role === "analyst" ? t.internalTicketDetails.roleAnalyst : t.internalTicketDetails.roleAttendant}
                      </SelectItem>
                    ))}
                </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setAssignmentDialogOpen(false);
                  if (ticket.assignee && ticket.assignee.id) {
                    setQuickAssignId(ticket.assignee.id.toString());
                  } else {
                    setQuickAssignId("");
                  }
                }}
              >
                {t.internalTicketDetails.cancel}
              </Button>
              <Button onClick={handleQuickAssign} disabled={isUpdating || !quickAssignId}>
                {isUpdating ? t.internalTicketDetails.saving : t.internalTicketDetails.confirm}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog de Reabertura */}
      {ticket && (
        <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t.internalTicketDetails.reopenTitle}</DialogTitle>
              <DialogDescription>
                {t.internalTicketDetails.reopenDialogDesc(ticket.id)}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="reopen_reason_dialog">{t.internalTicketDetails.reopenReasonLabel}</Label>
                <Textarea
                  id="reopen_reason_dialog"
                  placeholder={t.internalTicketDetails.reopenReasonPlaceholder}
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  rows={4}
                  className="mt-2"
                />
              </div>
              {ticket.can_reopen_until && (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t.internalTicketDetails.reopenDeadline} {new Date(ticket.can_reopen_until).toLocaleString()}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setReopenDialogOpen(false);
                  setReopenReason("");
                }}
              >
                {t.internalTicketDetails.cancel}
              </Button>
              <Button
                onClick={() => {
                  if (!reopenReason.trim()) {
                    toast({
                      title: t.internalTicketDetails.error,
                      description: t.internalTicketDetails.pleaseProvideReopen,
                      variant: "destructive",
                    });
                    return;
                  }
                  handleReopenTicket();
                  setReopenDialogOpen(false);
                }}
                disabled={isUpdating || !reopenReason.trim()}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isUpdating ? t.internalTicketDetails.saving : t.internalTicketDetails.reopenTicket}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default InternalTicketDetails;

